import {
    Box,
    Checkbox,
    Divider,
    Fab,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import AddIcon from '@mui/icons-material/Add';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CallPriorityField from '../components/users/CallPriorityField';
import { useUser } from '../hooks/useUser';
import { createUser } from '../services/users';
import { normalizeCallPriority } from '../utils/callPriority';

const clinicianTypeOptions = [
    { value: 'NURSING', label: 'Enfermagem' },
    { value: 'CARDIOLOGY', label: 'Cardiologia' },
    { value: 'GERIATRICS', label: 'Geriatria' },
    { value: 'NUTRITION', label: 'Nutrição' },
    { value: 'NEUROLOGY', label: 'Neurologia' },
    { value: 'PSYCHIATRY', label: 'Psiquiatria' },
    { value: 'PSYCHOLOGY', label: 'Psicologia' },
    { value: 'PHYSICAL_THERAPY', label: 'Fisioterapia' },
    { value: 'OCCUPATIONAL_THERAPY', label: 'Terapia Ocupacional' },
    { value: 'SOCIAL_ASSISTANT', label: 'Assistente Social' },
    { value: 'GENERAL_PRACTICE', label: 'Medicina Geral' }
];

const CreateDoctor = () => {
    const navigate = useNavigate();
    const { user } = useUser();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [form, setForm] = useState({
        name: '',
        email: '',
        birthDate: '',
        street: '',
        zip: '',
        city: '',
        province: '',
        country: '',
        phone: '',
        professionalCard: '',
        professionalCardExpiry: '',
        workplace: '',
        clinicianType: '',
        callPriority: 5,
        coordinator: false,
        notes: ''
    });

    const onFieldChange = (field) => (event) => {
        const value =
            event?.target?.type === 'checkbox' ? event.target.checked : event.target.value;
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const toApiDate = (date) => {
        if (!date) {
            return undefined;
        }

        if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
            return date;
        }

        const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(date);
        if (!match) {
            return undefined;
        }

        return `${match[3]}-${match[2]}-${match[1]}`;
    };

    const toPickerValue = (value) => {
        if (!value) {
            return null;
        }

        const parsed = dayjs(value);
        return parsed.isValid() ? parsed : null;
    };

    const onSubmit = async () => {
        if (!user.selectedRole) {
            setError('Sem role ativa para executar esta ação.');
            return;
        }

        if (!form.name.trim() || !form.email.trim()) {
            setError('Nome e Email são obrigatórios.');
            return;
        }

        const role = form.coordinator ? 'ACCOUNT_ADMIN' : 'CLINIC_CARE_GIVER';

        setSubmitting(true);
        setError('');

        try {
            await createUser({
                userRoleUuid: user.selectedRole,
                payload: {
                    name: form.name.trim(),
                    email: form.email.trim(),
                    role,
                    clinician_type: form.clinicianType || undefined,
                    call_priority: normalizeCallPriority(form.callPriority),
                    birth_date: toApiDate(form.birthDate),
                    phone: form.phone.trim() || undefined,
                    address: {
                        street: form.street.trim() || undefined,
                        zip: form.zip.trim() || undefined,
                        city: form.city.trim() || undefined,
                        province: form.province.trim() || undefined,
                        country: form.country.trim().toUpperCase() || undefined
                    },
                    user_data: {
                        professional_card: form.professionalCard.trim() || null,
                        professional_card_expiry: form.professionalCardExpiry.trim() || null,
                        workplace: form.workplace.trim() || null,
                        notes: form.notes.trim() || null,
                        is_coordinator: form.coordinator
                    }
                }
            });
            navigate('/doctors');
        } catch (err) {
            setError(err?.response?.data?.description || 'Nao foi possivel criar especialista.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 1.5, md: 2 }, bgcolor: '#ffffff' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Paper
                    variant="outlined"
                sx={{
                    mt: 2,
                    p: { xs: 1.5, md: 2.5 },
                    bgcolor: '#ffffff',
                    borderRadius: 2
                }}
                >
                <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                    <IconButton
                        sx={{
                            width: 40,
                            height: 40,
                            border: '1.5px solid #BDBDBD',
                            bgcolor: '#FFFFFF',
                            color: '#3D3D3D',
                            borderRadius: '12px',
                            '&:hover': {
                                bgcolor: '#F7F7F7'
                            }
                        }}
                        onClick={() => navigate(-1)}
                    >
                        <ArrowBackIosNewRoundedIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="h4" fontWeight={700}>
                        Criar Novo Membro
                    </Typography>
                </Stack>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                        gap: 2
                    }}
                >
                    <TextField
                        label="Nome"
                        placeholder="Nome completo"
                        fullWidth
                        value={form.name}
                        onChange={onFieldChange('name')}
                    />
                    <TextField
                        label="Email"
                        placeholder="utilizador@dominio.com"
                        fullWidth
                        value={form.email}
                        onChange={onFieldChange('email')}
                    />
                    <DatePicker
                        label="Data de Nascimento"
                        format="DD/MM/YYYY"
                        value={toPickerValue(form.birthDate)}
                        onChange={(newValue) =>
                            onFieldChange('birthDate')({
                                target: {
                                    value:
                                        newValue && dayjs(newValue).isValid()
                                            ? dayjs(newValue).format('YYYY-MM-DD')
                                            : ''
                                }
                            })
                        }
                        slotProps={{ textField: { fullWidth: true } }}
                    />
                    <TextField
                        label="Morada"
                        placeholder="Rua / Avenida"
                        fullWidth
                        sx={{ gridColumn: { xs: 'auto', md: 'span 2' } }}
                        value={form.street}
                        onChange={onFieldChange('street')}
                    />
                    <TextField
                        label="Código Postal"
                        placeholder="0000-000"
                        fullWidth
                        value={form.zip}
                        onChange={onFieldChange('zip')}
                    />
                    <TextField
                        label="Cidade"
                        placeholder="Cidade"
                        fullWidth
                        value={form.city}
                        onChange={onFieldChange('city')}
                    />
                    <TextField
                        label="Distrito / Provincia"
                        placeholder="Distrito ou provincia"
                        fullWidth
                        value={form.province}
                        onChange={onFieldChange('province')}
                    />
                    <TextField
                        label="País"
                        placeholder="PT"
                        fullWidth
                        value={form.country}
                        onChange={onFieldChange('country')}
                        inputProps={{ maxLength: 2 }}
                    />
                    <TextField
                        label="Telefone"
                        placeholder="912345678"
                        fullWidth
                        value={form.phone}
                        onChange={onFieldChange('phone')}
                    />
                </Box>

                <Divider sx={{ my: 4, borderColor: '#7f7f7f' }} />

                <Typography variant="h4" fontWeight={700} mb={3}>
                    Profissional
                </Typography>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                        gap: 2
                    }}
                >
                    <TextField
                        label="Cédula"
                        placeholder="Número da cédula"
                        fullWidth
                        value={form.professionalCard}
                        onChange={onFieldChange('professionalCard')}
                    />
                    <TextField
                        label="Validade"
                        placeholder="dd/mm/aaaa"
                        fullWidth
                        value={form.professionalCardExpiry}
                        onChange={onFieldChange('professionalCardExpiry')}
                    />
                    <Box />
                    <TextField
                        label="Local"
                        fullWidth
                        sx={{ gridColumn: { xs: 'auto', md: 'span 2' } }}
                        value={form.workplace}
                        onChange={onFieldChange('workplace')}
                    />
                    <Box />
                    <FormControl fullWidth>
                        <InputLabel id="especialidade-label">Especialidade</InputLabel>
                        <Select
                            labelId="especialidade-label"
                            label="Especialidade"
                            value={form.clinicianType}
                            onChange={onFieldChange('clinicianType')}
                        >
                            {clinicianTypeOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControlLabel
                        sx={{
                            m: 0,
                            minHeight: 56,
                            px: 1,
                            border: '1px solid',
                            borderColor: 'rgba(0, 0, 0, 0.23)',
                            borderRadius: 1,
                            alignItems: 'center'
                        }}
                        control={
                            <Checkbox
                                checked={form.coordinator}
                                onChange={onFieldChange('coordinator')}
                            />
                        }
                        label="Coordenador"
                    />
                    <TextField
                        label="Observações"
                        placeholder="Notas relevantes"
                        fullWidth
                        multiline
                        minRows={2}
                        sx={{ gridColumn: { xs: 'auto', md: 'span 2' } }}
                        value={form.notes}
                        onChange={onFieldChange('notes')}
                    />
                </Box>

                <Divider sx={{ my: 4, borderColor: '#7f7f7f' }} />

                <Typography variant="h4" fontWeight={700} mb={3}>
                    Definições de Videochamada
                </Typography>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
                        gap: 2
                    }}
                >
                    <CallPriorityField
                        value={form.callPriority}
                        onChange={(value) =>
                            setForm((prev) => ({
                                ...prev,
                                callPriority: value
                            }))
                        }
                        sx={{ maxWidth: 380 }}
                    />
                </Box>

                {error ? (
                    <Typography color="error" sx={{ mt: 2 }}>
                        {error}
                    </Typography>
                ) : null}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
                    <Fab
                        size="medium"
                        sx={{ bgcolor: '#D63731', color: '#fff' }}
                        onClick={onSubmit}
                        disabled={submitting}
                    >
                        <AddIcon />
                    </Fab>
                </Box>
            </Paper>
            </LocalizationProvider>
        </Box>
    );
};

export default CreateDoctor;
